import {
	ArrowPathIcon,
	Battery50Icon,
	ChartBarIcon,
	PaperAirplaneIcon,
	PlayIcon,
	UserCircleIcon,
	WifiIcon
} from '@heroicons/react/20/solid'
import React, { useEffect, useRef, useState } from 'react'
import { parseTextToHTML } from '../../../../libs/parse'
import { CARD_TYPE, CAROUSEL_TYPE, LINK_BUTTON, PHONE_CALL_BUTTON, REPLY_BUTTON, TEXT_TYPE } from './constants'

const Header = () => {
  return (
    <header className="bg-white shadow z-10 px-4 py-2 text-grey-blue flex-shrink-0">
      <div id="phoneInfo" className="flex">
        <p className="text-xs font-bold">1:37</p>
        <div id="phoneStatus" className="ml-auto flex gap-1">
          <WifiIcon className="h-4 w-4" />
          <ChartBarIcon className="h-4 w-4" />
          <Battery50Icon className="h-4 w-4" />
        </div>
      </div>
      <UserCircleIcon className="h-6 w-6 mx-auto" />
    </header>
  )
}

const UserMessage = ({ text }) => {
  return (
    <div className="flex justify-end">
      <div className="bg-indigo-500 text-white rounded-2xl rounded-br-sm px-3 py-2 max-w-[80%]">
        <p className="text-xs">{text}</p>
      </div>
    </div>
  )
}

const BotTextMessage = ({ message, isFirst }) => {
  return (
    <div className="flex gap-2">
      {isFirst && <UserCircleIcon className="h-6 w-6 text-grey-blue flex-shrink-0" />}
      {!isFirst && <div className="w-6" />}
      <div className="bg-blue-light/25 rounded-2xl rounded-bl-sm px-3 py-2 max-w-[85%]">
        <p className="text-xs whitespace-pre-wrap">{parseTextToHTML(message.text || '')}</p>
      </div>
    </div>
  )
}

const RichCardMessage = ({ card, onButtonClick }) => {
  return (
    <div className="flex gap-2">
      <div className="w-6" />
      <div className="flex flex-col border border-gray-200 rounded-xl overflow-hidden max-w-[85%] shadow-sm">
        {(card.image?.preview || card.image?.url) && (
          <img
            src={card.image.preview || card.image.url}
            alt={card.title || 'image'}
            className="w-full h-24 object-cover"
          />
        )}
        <div className="p-2 bg-white">
          {card.title && <h3 className="text-xs font-semibold">{card.title}</h3>}
          {card.description && (
            <p className="text-[10px] text-gray-500 mt-0.5">{card.description}</p>
          )}
          {card.buttons && card.buttons.length > 0 && (
            <div className="flex flex-col mt-2 pt-1 border-t border-gray-100">
              {card.buttons.map((button, idx) => (
                <InteractiveButton
                  key={idx}
                  button={button}
                  onClick={onButtonClick}
                  variant="card"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const CarouselMessage = ({ carousel, onButtonClick }) => {
  return (
    <div className="flex gap-2">
      <div className="w-6" />
      <div className="flex gap-2 overflow-x-auto pb-2 -mr-3 pr-3">
        {carousel.cards?.map((card, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 w-36 flex flex-col border border-gray-200 rounded-xl overflow-hidden shadow-sm"
          >
            {(card.image?.preview || card.image?.url) && (
              <img
                src={card.image.preview || card.image.url}
                alt={card.title || 'image'}
                className="w-full h-20 object-cover"
              />
            )}
            <div className="p-2 bg-white flex-1 flex flex-col">
              {card.title && (
                <h3 className="text-[10px] font-semibold line-clamp-2">{card.title}</h3>
              )}
              {card.buttons && card.buttons.length > 0 && (
                <div className="flex flex-col mt-auto pt-1">
                  {card.buttons.slice(0, 1).map((button, bidx) => (
                    <InteractiveButton
                      key={bidx}
                      button={button}
                      onClick={onButtonClick}
                      variant="carousel"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const InteractiveButton = ({ button, onClick, variant = 'suggestion' }) => {
  const handleClick = () => {
    if (button.type === REPLY_BUTTON && button.replyFlow) {
      onClick?.(button)
    } else if (button.type === LINK_BUTTON && button.link) {
      window.open(button.link, '_blank')
    } else if (button.type === PHONE_CALL_BUTTON && button.phone) {
      window.open(`tel:${button.phone}`, '_self')
    }
  }

  const baseClasses = 'cursor-pointer transition-all duration-200 active:scale-95'

  if (variant === 'suggestion') {
    return (
      <button
        onClick={handleClick}
        className={`${baseClasses} border border-indigo-300 text-indigo-600 px-2 py-1 text-[10px] rounded-full whitespace-nowrap hover:bg-indigo-50 hover:border-indigo-400`}
      >
        {button.text}
      </button>
    )
  }

  if (variant === 'card' || variant === 'carousel') {
    return (
      <button
        onClick={handleClick}
        className={`${baseClasses} text-indigo-500 text-[10px] py-0.5 text-center hover:text-indigo-700`}
      >
        {button.text}
      </button>
    )
  }

  return null
}

const Suggestions = ({ suggestions, onButtonClick }) => {
  return (
    <div className="-mx-3 mt-1">
      <div className="flex px-3 gap-1.5 overflow-x-auto pb-1">
        {suggestions.map((suggestion, idx) => (
          <InteractiveButton
            key={idx}
            button={suggestion}
            onClick={onButtonClick}
            variant="suggestion"
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Flow options selector - shown after user types text to choose the next flow
 */
const FlowOptionsSelector = ({ options, onSelect }) => {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2">
      <p className="text-[10px] text-amber-700 font-medium mb-2 text-center">
        Select the response path to simulate:
      </p>
      <div className="flex flex-col gap-1.5">
        {options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => onSelect(option.flowId)}
            className={`text-[10px] px-3 py-2 rounded-lg text-left transition-all ${
              option.type === 'failure'
                ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-200'
                : option.type === 'success'
                ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const TypingIndicator = () => {
  return (
    <div className="flex gap-2">
      <UserCircleIcon className="h-6 w-6 text-grey-blue flex-shrink-0" />
      <div className="bg-blue-light/25 rounded-2xl px-3 py-2">
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  )
}

/**
 * Find possible next flows when user types free text
 * Returns an array of possible destinations based on the template structure
 */
const findPossibleNextFlows = (currentFlowId, rbmFlows, rbmFlowAliases) => {
  const allFlowIds = Object.keys(rbmFlows).filter(
    (id) => id !== 'rbmFlowAliasesMap' && id !== currentFlowId && id !== 'init'
  )

  // Look for flows that might be related based on naming patterns
  const currentBase = currentFlowId.toLowerCase()
  
  // Common patterns for success/failure flows
  const successPatterns = ['success', 'ok', 'correct', 'valid', 'check', 'reason', 'next']
  const failurePatterns = ['fail', 'error', 'invalid', 'wrong', 'retry', 'try', 'second', 'last']
  
  const categorizedFlows = {
    success: [],
    failure: [],
    other: []
  }

  allFlowIds.forEach((flowId) => {
    const flowLower = flowId.toLowerCase()
    const flowContent = rbmFlows[flowId]
    
    // Skip empty flows
    if (!flowContent || flowContent.length === 0) return
    
    // Check if this flow is connected via buttons from another flow
    const isSuccessLike = successPatterns.some(p => flowLower.includes(p))
    const isFailureLike = failurePatterns.some(p => flowLower.includes(p))
    
    if (isFailureLike) {
      categorizedFlows.failure.push(flowId)
    } else if (isSuccessLike) {
      categorizedFlows.success.push(flowId)
    } else {
      categorizedFlows.other.push(flowId)
    }
  })

  // Build a list of options to show the user
  const options = []

  // If we have failure flows, add them as options
  categorizedFlows.failure.forEach((flowId) => {
    const displayName = rbmFlowAliases[flowId] || flowId
    options.push({
      flowId,
      label: `❌ ${displayName}`,
      type: 'failure'
    })
  })

  // If we have success flows, add them
  categorizedFlows.success.forEach((flowId) => {
    const displayName = rbmFlowAliases[flowId] || flowId
    options.push({
      flowId,
      label: `✓ ${displayName}`,
      type: 'success'
    })
  })

  // Add other flows if no specific ones found
  if (options.length === 0) {
    categorizedFlows.other.slice(0, 3).forEach((flowId) => {
      const displayName = rbmFlowAliases[flowId] || flowId
      options.push({
        flowId,
        label: displayName,
        type: 'other'
      })
    })
  }

  return options
}

/**
 * Check if current flow expects text input (no suggestions)
 */
const flowExpectsTextInput = (flowElements) => {
  if (!flowElements || flowElements.length === 0) return false
  
  const lastElement = flowElements[flowElements.length - 1]
  
  if (!lastElement.suggestions || lastElement.suggestions.length === 0) {
    if (lastElement.type === CARD_TYPE && lastElement.buttons?.length > 0) {
      return false
    }
    if (lastElement.type === CAROUSEL_TYPE) {
      const hasButtons = lastElement.cards?.some((c) => c.buttons?.length > 0)
      if (hasButtons) return false
    }
    return true
  }
  
  return false
}

const InteractiveDemo = ({ rbmFlows, rbmFlowAliases = {} }) => {
  const [conversationHistory, setConversationHistory] = useState([])
  const [currentSuggestions, setCurrentSuggestions] = useState([])
  const [currentFlowId, setCurrentFlowId] = useState('init')
  const [isTyping, setIsTyping] = useState(false)
  const [inputText, setInputText] = useState('')
  const [expectsTextInput, setExpectsTextInput] = useState(false)
  const [demoStarted, setDemoStarted] = useState(false)
  const [pendingUserText, setPendingUserText] = useState(null) // Text waiting for flow selection
  const [flowOptions, setFlowOptions] = useState([]) // Possible next flows after text input
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversationHistory, isTyping])

  // Reset when flows change
  useEffect(() => {
    resetDemo()
  }, [rbmFlows])

  const startDemo = () => {
    setDemoStarted(true)
    setConversationHistory([])
    setCurrentSuggestions([])
    setCurrentFlowId('init')
    setIsTyping(true)
    setInputText('')
    setExpectsTextInput(false)
    setPendingUserText(null)
    setFlowOptions([])

    setTimeout(() => {
      const initFlow = rbmFlows['init'] || []
      if (initFlow.length > 0) {
        addFlowMessagesToHistory(initFlow, 'init', true)
      }
      setIsTyping(false)
    }, 500)
  }

  const resetDemo = () => {
    setDemoStarted(false)
    setConversationHistory([])
    setCurrentSuggestions([])
    setCurrentFlowId('init')
    setPendingUserText(null)
    setFlowOptions([])
  }

  const addFlowMessagesToHistory = (flowElements, flowId, isFirst = false) => {
    const newMessages = []
    let suggestions = []

    flowElements.forEach((element, idx) => {
      if (!element) return // Skip undefined/null elements
      newMessages.push({
        type: 'bot',
        element: element,
        isFirst: isFirst && idx === 0
      })

      if (element.suggestions && element.suggestions.length > 0) {
        suggestions = element.suggestions
      }
    })

    setConversationHistory((prev) => [...prev, ...newMessages])
    setCurrentSuggestions(suggestions)
    setCurrentFlowId(flowId)
    
    const needsText = flowExpectsTextInput(flowElements)
    setExpectsTextInput(needsText)
    
    if (needsText) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }

  const navigateToFlow = (flowId, userMessage = null) => {
    if (userMessage) {
      setConversationHistory((prev) => [
        ...prev,
        { type: 'user', text: userMessage }
      ])
    }
    
    setCurrentSuggestions([])
    setExpectsTextInput(false)
    setIsTyping(true)

    setTimeout(() => {
      const targetFlow = rbmFlows[flowId]
      if (targetFlow && targetFlow.length > 0) {
        addFlowMessagesToHistory(targetFlow, flowId, false)
      } else {
        setConversationHistory((prev) => [
          ...prev,
          {
            type: 'bot',
            element: { 
              type: TEXT_TYPE, 
              text: `[Flow "${flowId}" is empty or not configured]` 
            },
            isFirst: false
          }
        ])
        setExpectsTextInput(true)
      }
      setIsTyping(false)
    }, 600)
  }

  const handleButtonClick = (button) => {
    const isCallMeFlow =
      String(currentFlowId || '').toLowerCase() === 'callme'
    const scheduleMap = {
      manana: 'Manana',
      mediodia: 'Mediodia',
      tarde: 'Tarde'
    }
    const normalizedText = String(button?.text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
    const selectedSchedule = scheduleMap[normalizedText]

    if (isCallMeFlow && selectedSchedule) {
      const subject = encodeURIComponent(
        `RCS Demo - Solicitud de contacto (${selectedSchedule})`
      )
      const body = encodeURIComponent(
        `Horario seleccionado en demo: ${selectedSchedule}`
      )
      window.location.href = `mailto:alftpa@gmail.com?subject=${subject}&body=${body}`
    }

    if (!button.replyFlow) return
    navigateToFlow(button.replyFlow, button.text)
  }

  const handleTextSubmit = (e) => {
    e.preventDefault()
    if (!inputText.trim()) return

    const text = inputText.trim()
    setInputText('')

    // Add user message to history
    setConversationHistory((prev) => [
      ...prev,
      { type: 'user', text: text }
    ])

    // Find possible next flows
    const options = findPossibleNextFlows(currentFlowId, rbmFlows, rbmFlowAliases)

    if (options.length > 0) {
      // Show flow options to user
      setPendingUserText(text)
      setFlowOptions(options)
      setExpectsTextInput(false)
    } else {
      // No flows found - end of conversation
      setConversationHistory((prev) => [
        ...prev,
        {
          type: 'bot',
          element: { 
            type: TEXT_TYPE, 
            text: `Gracias por tu mensaje. Has llegado al final de esta rama del flujo.` 
          },
          isFirst: false
        }
      ])
      setCurrentSuggestions([])
      setExpectsTextInput(false)
    }
  }

  const handleFlowOptionSelect = (flowId) => {
    setPendingUserText(null)
    setFlowOptions([])
    navigateToFlow(flowId)
  }

  const renderMessage = (msg, idx) => {
    if (msg.type === 'user') {
      return <UserMessage key={idx} text={msg.text} />
    }

    const element = msg.element
    switch (element.type) {
      case TEXT_TYPE:
        return (
          <BotTextMessage key={idx} message={element} isFirst={msg.isFirst} />
        )
      case CARD_TYPE:
        return (
          <RichCardMessage
            key={idx}
            card={element}
            onButtonClick={handleButtonClick}
          />
        )
      case CAROUSEL_TYPE:
        return (
          <>
            {element.text && (
              <BotTextMessage key={`${idx}-text`} message={element} isFirst={msg.isFirst} />
            )}
            <CarouselMessage
              key={idx}
              carousel={element}
              onButtonClick={handleButtonClick}
            />
          </>
        )
      default:
        return null
    }
  }

  const hasContent = rbmFlows && Object.keys(rbmFlows).filter(k => k !== 'rbmFlowAliasesMap').length > 0

  const getCurrentFlowName = () => {
    if (rbmFlowAliases[currentFlowId]) return rbmFlowAliases[currentFlowId]
    if (currentFlowId === 'init') return 'Start'
    return currentFlowId
  }

  return (
    <div className="flex flex-col w-[280px] border-4 border-grey-blue/30 rounded-3xl shadow-xl bg-white overflow-hidden h-full">
      <Header />

      {/* Reset button */}
      <button
        onClick={resetDemo}
        className="absolute top-14 right-4 z-20 bg-white/90 hover:bg-white border border-gray-200 rounded-full p-1.5 shadow-sm transition-all hover:scale-110"
        title="Reset demo"
      >
        <ArrowPathIcon className="h-3.5 w-3.5 text-gray-600" />
      </button>

      {/* Current flow indicator */}
      <div className="absolute top-14 left-4 z-20 bg-indigo-100 text-indigo-700 text-[9px] px-2 py-0.5 rounded-full font-medium">
        {getCurrentFlowName()}
      </div>

      {/* Messages area - single scroll container */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col p-3 gap-2 pt-8 pb-2">
          {!hasContent ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <p className="text-gray-400 text-xs">No messages yet</p>
              <p className="text-gray-300 text-[10px] mt-1">Add elements to preview</p>
            </div>
          ) : !demoStarted ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <button
                onClick={startDemo}
                className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              >
                <PlayIcon className="h-8 w-8 text-white ml-1" />
              </button>
              <p className="text-gray-500 text-xs mt-3 font-medium">Start Demo</p>
            </div>
          ) : (
            <>
              {conversationHistory.map((msg, idx) => renderMessage(msg, idx))}
              {isTyping && <TypingIndicator />}
              {!isTyping && flowOptions.length > 0 && (
                <FlowOptionsSelector
                  options={flowOptions}
                  onSelect={handleFlowOptionSelect}
                />
              )}
              {!isTyping && currentSuggestions.length > 0 && flowOptions.length === 0 && (
                <Suggestions
                  suggestions={currentSuggestions}
                  onButtonClick={handleButtonClick}
                />
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Text input area */}
      {demoStarted && (
        <form onSubmit={handleTextSubmit} className="border-t border-gray-200 p-2 bg-gray-50 flex-shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={expectsTextInput ? "Type your response..." : "Type a message..."}
              className={`flex-1 text-xs px-3 py-2 border rounded-full focus:outline-none focus:ring-1 ${
                expectsTextInput 
                  ? 'border-indigo-300 focus:border-indigo-400 focus:ring-indigo-400 bg-indigo-50/50' 
                  : 'border-gray-200 focus:border-indigo-400 focus:ring-indigo-400'
              }`}
            />
            <button
              type="submit"
              disabled={!inputText.trim()}
              className={`p-2 rounded-full transition-colors ${
                inputText.trim()
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400'
              }`}
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          </div>
        </form>
      )}

      {/* Demo indicator */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-center py-1.5 text-[10px] font-medium flex-shrink-0">
        Interactive Demo
      </div>
    </div>
  )
}

export default InteractiveDemo
